import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import Header from "../components/Header";
import PasswordStrength from "../components/PasswordStrength";
import { ArrowRight, Eye, EyeOff, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import SEOHead from "../components/SEOHead";
import { useToast } from "../hooks/useToast";

// Input sanitization helper
function sanitizeInput(value) {
  return value.replace(/[<>"'&]/g, (char) => {
    const map = { "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#x27;", "&": "&amp;" };
    return map[char] || char;
  });
}

export default function Register() {
  const { register, setUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const formErrorId = "register-form-error";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);

    const cleanName = sanitizeInput(name.trim());
    const cleanEmail = sanitizeInput(email.trim());
    const cleanPassword = sanitizeInput(password);

    const r = await register(cleanEmail, cleanPassword, cleanName);
    setLoading(false);

    if (r.ok) {
      setSuccess(true);
      addToast({
        type: "success",
        title: t("register.account_created", "Account created!"),
        description: t("register.welcome", "Welcome to Max Employee."),
      });
      setTimeout(() => navigate("/dashboard", { replace: true }), 1200);
    } else {
      setError(r.error);
      addToast({
        type: "error",
        title: t("register.register_failed", "Registration failed"),
        description: r.error || t("register.try_again", "Please try again."),
      });
    }
  };

  const onGoogle = () => {
    const errMsg = t("register.google_unavailable", "Google sign-up is temporarily unavailable. Use email to continue.");
    setError(errMsg);
    addToast({
      type: "warning",
      title: t("register.google_failed", "Google sign-up unavailable"),
      description: errMsg,
    });
  };

  // Success animation overlay
  if (success) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center">
      <SEOHead
        title={t('register.sign_up_max_employee')}
        description={t('register.create_your_max_employee_account_start_your_7_day')}
        url="/register"
        noIndex
      />
        <div className="text-center animate-pulse" role="status" aria-live="polite">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" aria-hidden="true" />
          <h2 className="font-display text-2xl uppercase">{t('register.account_created', 'Account Created!')}</h2>
          <p className="text-zinc-400 text-sm mt-2">{t('register.redirecting', 'Redirecting to dashboard...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />
      <main id="main-content" className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
        <div className="hidden lg:block" aria-hidden="true">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-8">// PROVISION ACCESS</div>
          <h1 className="font-display text-5xl xl:text-6xl uppercase leading-[0.95]">Provision<br/>your first<br/><span className="text-amber-500">{t('auth.agent_dot')}</span></h1>
          <p className="mt-8 text-zinc-400 max-w-md leading-relaxed">
            {t('register.subtitle', 'Create an account to purchase, download, and deploy your AI digital worker.')}
          </p>
        </div>

        <div className="border border-zinc-900 bg-zinc-950 p-8 md:p-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3" aria-hidden="true">// REGISTER</div>
          <h2 className="font-display text-3xl uppercase mb-8" id="register-heading">{t('register.create_account', 'Create account')}</h2>

          <button
            data-testid="google-register-button"
            onClick={onGoogle}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white text-black hover:bg-amber-500 disabled:opacity-50 px-6 py-4 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            {t('register.continue_google', 'Continue with Google')}
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-zinc-900" aria-hidden="true" />
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">{t('register.or_email', 'or with email')}</div>
            <div className="flex-1 h-px bg-zinc-900" aria-hidden="true" />
          </div>

          {error && (
            <div
              id={formErrorId}
              role="alert"
              aria-live="assertive"
              className="border border-red-900 bg-red-950/30 text-red-400 px-4 py-3 font-mono text-xs mb-5"
            >
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5" aria-describedby={error ? formErrorId : undefined} noValidate>
            <div>
              <label htmlFor="register-name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">{t('register.name', 'NAME')}</label>
              <input
                id="register-name"
                data-testid="register-name-input"
                type="text"
                required
                aria-required="true"
                aria-invalid={error ? "true" : undefined}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-black border border-zinc-800 focus:border-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 px-4 py-3 text-white font-mono text-sm"
                placeholder={t('register.jane_doe')}
                autoComplete="name"
              />
            </div>
            <div>
              <label htmlFor="register-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">{t('register.email', 'EMAIL')}</label>
              <input
                id="register-email"
                data-testid="register-email-input"
                type="email"
                required
                aria-required="true"
                aria-invalid={error ? "true" : undefined}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black border border-zinc-800 focus:border-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 px-4 py-3 text-white font-mono text-sm"
                placeholder={t("register.email_placeholder", "you@company.com")}
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor="register-password" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">{t('register.password', 'PASSWORD')}</label>
              <div className="relative">
                <input
                  id="register-password"
                  data-testid="register-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  aria-required="true"
                  aria-invalid={error ? "true" : undefined}
                  aria-describedby="register-password-hint"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black border border-zinc-800 focus:border-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 px-4 py-3 pr-12 text-white font-mono text-sm"
                  placeholder={t('register.password_placeholder', 'At least 6 characters')}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-500 focus-visible:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-sm p-1 min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors"
                  aria-label={showPassword ? t('register.hide_password', 'Hide password') : t('register.show_password', 'Show password')}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p id="register-password-hint" className="font-mono text-[10px] text-zinc-500 mt-1">
                {t('register.password_hint', 'Minimum 6 characters')}
              </p>
              <PasswordStrength password={password} />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full group flex items-center justify-between bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black px-6 py-4 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
            >
              <span>{loading ? t('register.creating', 'Creating...') : t('register.create_account_button', 'Create account')}</span>
              {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-900 text-center">
            <span className="text-zinc-400 text-sm">{t('register.has_account', 'Have an account?')} </span>
            <Link to="/login" data-testid="link-login" className="text-amber-500 font-mono text-xs uppercase tracking-wider hover:text-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
              {t('register.sign_in', 'Sign in')} →
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
